import { useEffect, useId, useState } from 'react';

import { Button } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Select } from '@ds/forms';
import { useAviso } from '@/comuns/toast';
import { etapasAtivas } from '@/data/funis';
import { dados } from '@/data/porta';
import type { Funil } from '@/data/tipos';

export interface ArquivarEtapaModalProps {
    funil: Funil;
    etapa: { id: string; titulo: string } | null;
    /** Leads nela no recorte carregado. O servidor confere de novo ao arquivar. */
    quantidade: number;
    onClose: () => void;
    onArquivada: () => void;
}

/**
 * Arquivar etapa aberta — trilha T7.
 *
 * Com leads na etapa, dizer para onde eles vão é obrigatório, e todos mudam numa
 * transação só (`arquivar_etapa`, 0100), cada um com o movimento no histórico.
 * O destino é outra etapa ativa do mesmo pipeline.
 */
export function ArquivarEtapaModal({ funil, etapa, quantidade, onClose, onArquivada }: ArquivarEtapaModalProps) {
    const aviso = useAviso();
    const idDestino = useId();
    const [destino, setDestino] = useState('');
    const [erro, setErro] = useState<string | null>(null);
    const [arquivando, setArquivando] = useState(false);

    useEffect(() => {
        setDestino('');
        setErro(null);
    }, [etapa]);

    if (!etapa) return null;

    const opcoes = etapasAtivas(funil).filter((e) => e.id !== etapa.id).map((e) => ({ value: e.id, label: e.titulo }));
    const precisaDeDestino = quantidade > 0;

    const arquivar = async () => {
        setArquivando(true);
        setErro(null);
        try {
            const movidos = await dados().arquivarEtapa(etapa.id, destino || undefined);
            const para = opcoes.find((o) => o.value === destino)?.label;
            aviso({
                title: 'Etapa arquivada',
                description: movidos > 0 && para
                    ? `${etapa.titulo} · ${movidos} ${movidos === 1 ? 'lead foi' : 'leads foram'} para ${para}`
                    : etapa.titulo,
            });
            onArquivada();
            onClose();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível arquivar a etapa.');
        } finally {
            setArquivando(false);
        }
    };

    return (
        <Modal
            open onClose={onClose} width={460} title={`Arquivar "${etapa.titulo}"?`}
            description="A etapa sai do quadro e dos formulários. O histórico dos leads continua mostrando o nome dela."
            dirty={destino !== ''}
            footer={
                <>
                    <Button variant="secondary" onClick={onClose}>Cancelar</Button>
                    <Button onClick={() => void arquivar()} loading={arquivando} disabled={precisaDeDestino && !destino}>
                        Arquivar etapa
                    </Button>
                </>
            }
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {precisaDeDestino && (
                    <Alert tone="warning" dismissible={false}>
                        {quantidade} {quantidade === 1 ? 'lead está' : 'leads estão'} nesta etapa. Todos vão juntos para a etapa
                        escolhida, e cada um fica com o movimento no histórico.
                    </Alert>
                )}
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}
                <FormField
                    label="Para onde vão os leads" htmlFor={idDestino} required={precisaDeDestino}
                    hint={precisaDeDestino ? undefined : 'Nenhum lead nesta etapa agora.'}
                >
                    <Select id={idDestino} value={destino} onChange={(v) => { setDestino(v); setErro(null); }} options={opcoes} placeholder="Escolha a etapa" />
                </FormField>
            </div>
        </Modal>
    );
}
